import { Box, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { LoadingSpinner, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useHistory } from "react-router-dom";

import { PullToRefresh } from "../ShiftDiscovery/ListView/PullToRefresh";
import { SHIFT_DISCOVERY_LIST_PATH } from "../ShiftDiscovery/paths";
import { useScrollToTopOnStatusTap } from "../useScrollToTopOnStatusTap";
import { InHomeBaaDialog } from "./BaaDialog/Dialog";
import { useSignatures } from "./BaaDialog/useSignatures";
import { InHomeCommsBannerCallout } from "./CommsBanner/Callout";
import { BAA_SIGNATURE_NAME } from "./constants";
import { InHomeOpenVisitsList } from "./OpenVisits/List";
import { getVisitsQueryKey } from "./useGetVisitsWithCaseData";
import { getWorkerVisitLockQueryKey } from "./useGetWorkerVisitLockStatus";

export function FindVisitsPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { userId: workerId } = useDefinedWorker();

  const queryClient = useQueryClient();

  useScrollToTopOnStatusTap(scrollRef);
  const {
    data: signaturesData,
    isSuccess: isSuccessSignatures,
    isLoading: isLoadingSignatures,
  } = useSignatures();
  const baaModalState = useModalState("open");
  const hasSignedBaa =
    isSuccessSignatures &&
    signaturesData.data.some(
      (signature) => signature.attributes.documentName === BAA_SIGNATURE_NAME
    );

  if (isLoadingSignatures) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingSpinner size="medium" />
      </Box>
    );
  }

  return (
    <>
      {isSuccessSignatures && !hasSignedBaa && <InHomeBaaDialog modalState={baaModalState} />}
      <Stack
        sx={(theme) => ({
          flex: 1,
          backgroundColor: theme.palette.background.tertiary,
          overflow: "hidden",
          position: "relative",
        })}
      >
        <StickyScrollPageHeader
          title="In-Home Visits"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
          navigateBack={() => {
            history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
          }}
        />
        <PullToRefresh
          scrollRef={scrollRef}
          onRefresh={async () => {
            await Promise.all([
              queryClient.invalidateQueries(getVisitsQueryKey),
              queryClient.invalidateQueries(getWorkerVisitLockQueryKey(workerId)),
            ]);
          }}
        />
        <Stack
          ref={scrollRef}
          data-testid="home-health-find-visits-page-scroll-container"
          spacing={7}
          sx={{
            flex: 1,
            overflow: "auto",
            px: 5,
            pt: 6,
          }}
        >
          <InHomeCommsBannerCallout />
          {isSuccessSignatures && hasSignedBaa && <InHomeOpenVisitsList />}
        </Stack>
      </Stack>
    </>
  );
}
